import { useCreateDataPoint } from '../DataPoint';
import { UpdateNoteReceivableValueServiceInput, UpdateNoteReceivableServiceInput } from './types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCreateFileAttachment } from '../FileAttachment';
import { CreateNoteReceivableServiceInput } from './types';
import { useCreateNoteReceivableMutation, useDeleteNoteReceivableMutation } from '../generated/graphql';
import { useGraphqlClient } from '../graphql';
import { noteReceivableKeys } from './queryKeys';
import { useUpdateNoteReceivable } from './mutations';
import { ttDetailsKeys } from '../TtDetails';
import { scorecardKeys } from '../Scorecard';

export const useUpdateNoteReceivableValueService = () => {
  const { createDataPoint } = useCreateDataPoint();
  const queryClient = useQueryClient();

  const updateNoteReceivableValueService = useMutation({
    mutationKey: ['updateNoteReceivableValueService'],
    mutationFn: async (input: UpdateNoteReceivableValueServiceInput) => {
      await createDataPoint.mutateAsync({
        dataPoint: {
          dateTime: new Date().toISOString(),
          data: {
            monetaryAmountValue: {
              value: {
                currencyCode: 'USD',
                value: input.valueInCents,
              },
            },
          },
          groupID: input.valueDataPointGroupID,
        },
        tenantID: input.householdID,
      });
    },
    onSuccess: (_, { householdID, noteReceivableID }) => {
      queryClient.invalidateQueries(noteReceivableKeys.noteReceivable({ householdID, id: noteReceivableID }));
    },
  });

  return { updateNoteReceivableValueService };
};

export function useCreateNoteReceivableService() {
  const { createFileAttachment } = useCreateFileAttachment();
  const graphqlClient = useGraphqlClient();
  const createNoteReceivable = useCreateNoteReceivableMutation(graphqlClient);
  const deleteNoteReceivable = useDeleteNoteReceivableMutation(graphqlClient, { retry: 3 });
  const { updateNoteReceivableValueService } = useUpdateNoteReceivableValueService();
  const queryClient = useQueryClient();

  const createNoteReceivableService = useMutation({
    mutationFn: async ({
      files,
      createNoteReceivableMutationInput,
      valueInCents,
    }: CreateNoteReceivableServiceInput) => {
      const {
        createNoteReceivable: { noteReceivable },
      } = await createNoteReceivable.mutateAsync({ input: createNoteReceivableMutationInput });

      if (valueInCents) {
        try {
          await updateNoteReceivableValueService.mutateAsync({
            valueInCents,
            noteReceivableID: noteReceivable.id,
            householdID: noteReceivable.householdID,
            valueDataPointGroupID: noteReceivable.value.id,
          });
        } catch (error) {
          try {
            await deleteNoteReceivable.mutateAsync({
              input: { householdID: noteReceivable.householdID, id: noteReceivable.id },
            });
          } catch (rollbackError) {
            console.error('Unable to roll back note receivable after its initial value failed', {
              householdID: noteReceivable.householdID,
              noteReceivableID: noteReceivable.id,
              rollbackError,
            });
          }
          throw error;
        }
      }

      files?.forEach((file) => {
        createFileAttachment.mutate({
          tenantID: noteReceivable.householdID,
          fileAttachment: { itemID: noteReceivable.id, fileID: file.id },
        });
      });

      return noteReceivable;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(ttDetailsKeys.base);
      queryClient.invalidateQueries(scorecardKeys.base);
    },
  });

  return { createNoteReceivableService };
}

/**
 * @returns a function that:
 * 1. Updates a note receivable
 * 2. Creates a data point for the note receivable value data point group
 * 3. Creates file attachments for any pending files
 */
export const useUpdateNoteReceivableService = () => {
  const { updateNoteReceivableValueService } = useUpdateNoteReceivableValueService();
  const { updateNoteReceivable } = useUpdateNoteReceivable();
  const { createFileAttachment } = useCreateFileAttachment();
  const queryClient = useQueryClient();

  const updateNoteReceivableService = useMutation({
    mutationKey: ['updateNoteReceivableService'],
    mutationFn: async (input: UpdateNoteReceivableServiceInput) => {
      const { noteReceivable } = await updateNoteReceivable.mutateAsync(
        input.updateNoteReceivableInput,
        input.updateNoteReceivableMutationOptions
      );

      await updateNoteReceivableValueService.mutateAsync({
        householdID: noteReceivable.householdID,
        valueDataPointGroupID: noteReceivable.value.id,
        valueInCents: input.valueInCents,
        noteReceivableID: noteReceivable.id,
      });

      await Promise.all(
        (input.files || []).map((pendingFile) =>
          createFileAttachment.mutateAsync({
            tenantID: noteReceivable.householdID,
            fileAttachment: { fileID: pendingFile.id, itemID: noteReceivable.id },
          })
        )
      );
    },
    onSuccess: (_, { updateNoteReceivableInput: { householdID, id } }) => {
      queryClient.invalidateQueries(noteReceivableKeys.noteReceivable({ householdID, id }));
    },
  });

  return { updateNoteReceivableService };
};
